import Fetch from "@/components/fetch";

export interface Statistics {
  totalScientificPublications: number;
  totalScientificCitations: number;
  hIndex: number;
  qScore: {
    total: number;
    fromArticles: number;
    fromBooks: number;
  };
  iScore: number;
  iScoreWOS: number;
  impactFactor: {
    total: number;
    relative: number;
  };
  publicationsWithIF: number;
  singleAuthorPublicationsWithIF: number;
  publicationsByType: {
    [key: string]: {
      foreign: number;
      hungarianForeign: number;
      hungarianNative: number;
    };
  };
}

export interface Author {
  mtid: string;
  name: string;
  degrees: any[];
  identifiers: any[];
}

export interface AuthorSuggestion {
  id: number;
  name: string;
}

// Define types for your chart data
// MUI X Charts-oriented types
export interface RankingsChartData {
  years: string[];
  rankings: {
    D1: number[];
    Q1: number[];
    Q2: number[];
    Q3: number[];
    Q4: number[];
    "N/A": number[];
  };
}

export interface LineOrBarChartData {
  label: string;
  years: string[];
  data: (number | null)[];
}

export interface ChartSeriesRankings {
  year: string;
  D1: number;
  Q1: number;
  Q2: number;
  Q3: number;
  Q4: number;
  "N/A": number;
}

export type ChartData = {
  publicationsWithRankings: RankingsChartData;
  cumulativePublications: RankingsChartData;
  iScore: LineOrBarChartData;
  cumulativeIScore: LineOrBarChartData;
  qScore: LineOrBarChartData;
  cumulativeQScore: LineOrBarChartData;
  impactFactor: LineOrBarChartData;
  cumulativeImpactFactor: LineOrBarChartData;
};

// Meta information about analysis and datasets
export interface AnalysisMeta {
  lastUpdated: string; // datetime string with date and time
  ifListVersionYear: number | null;
  norwayListVersionYear: number | null;
  excludeNorwayLevel0: boolean;
}

export interface Publication {
  contributorOnly?: boolean;
  qscore?: number;
  iscore?: number;
  hasISBNISSN?: boolean;
  scientificCitationCount?: number;
  IFindex?: number;
  norwayLevel?: number;

  otype: string;
  mtid: number;
  status: string;
  published: boolean;
  unhandledTickets: number;
  deleted: boolean;
  lastRefresh: string;
  lastModified: string;
  created: string;
  creator?: {
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    familyName?: string;
    givenName?: string;
    published?: boolean;
    oldId?: number;
    snippet?: boolean;
  };
  lastDuplumSearch?: string;
  adminApproved?: string;
  adminApprover?: {
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    familyName?: string;
    givenName?: string;
    published?: boolean;
    snippet?: boolean;
  };
  tempLockers?: Array<{
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    familyName?: string;
    givenName?: string;
    published?: boolean;
    snippet?: boolean;
  }>;
  tempLocked?: string;
  core?: boolean;
  citation?: boolean;
  publicationPending?: boolean;
  type: {
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    code?: number;
    otypeName?: string;
    listPosition?: number;
    published?: boolean;
    oldId?: number;
    snippet?: boolean;
  };
  subType?: {
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    name?: string;
    nameEng?: string;
    docType?: {
      otype?: string;
      mtid?: number;
      link?: string;
      label?: string;
      code?: number;
      otypeName?: string;
      listPosition?: number;
      published?: boolean;
      oldId?: number;
      snippet?: boolean;
    };
    listPosition?: number;
    published?: boolean;
    oldId?: number;
    snippet?: boolean;
  };
  category?: {
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    published?: boolean;
    oldId?: number;
    snippet?: boolean;
  };
  languages?: Array<{
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    name?: string;
    nameEng?: string;
    published?: boolean;
    oldId?: number;
    snippet?: boolean;
  }>;
  firstAuthor?: string;
  authorships?: Array<{
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    listPosition?: number;
    share?: number;
    first?: boolean;
    last?: boolean;
    corresponding?: boolean;
    author?: {
      otype?: string;
      mtid?: number;
      link?: string;
      label?: string;
      familyName?: string;
      givenName?: string;
      published?: boolean;
      oldId?: number;
      snippet?: boolean;
    };
    familyName?: string;
    givenName?: string;
    authorTyped?: boolean;
    editorTyped?: boolean;
    otherTyped?: boolean;
    type?: {
      otype?: string;
      mtid?: number;
      link?: string;
      label?: string;
      code?: number;
      published?: boolean;
      oldId?: number;
      snippet?: boolean;
    };
    published?: boolean;
    snippet?: boolean;
  }>;
  title: string;
  identifiers?: Array<{
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    source?: {
      otype?: string;
      mtid?: number;
      link?: string;
      label?: string;
      type?: {
        otype?: string;
        mtid?: number;
        link?: string;
        label?: string;
        mayHaveOa?: boolean;
        published?: boolean;
        snippet?: boolean;
      };
      name?: string;
      nameEng?: string;
      linkPattern?: string;
      publiclyVisible?: boolean;
      published?: boolean;
      oldId?: number;
      snippet?: boolean;
    };
    validState?: string;
    idValue?: string;
    realUrl?: string;
    oaType?: string;
    published?: boolean;
    snippet?: boolean;
  }>;
  citations?: Array<{
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    related?: {
      otype?: string;
      mtid?: number;
      link?: string;
      label?: string;
      core?: boolean;
      citation?: boolean;
      publicationPending?: boolean;
      type?: {
        otype?: string;
        mtid?: number;
        link?: string;
        label?: string;
        code?: number;
        otypeName?: string;
        listPosition?: number;
        published?: boolean;
        oldId?: number;
        snippet?: boolean;
      };
      identifiers?: Array<{
        source?: {
          otype?: string;
          mtid?: number;
          link?: string;
          label?: string;
        };
      }>;
    };
    source?: string;
    externalCitation?: boolean;
    mentionCount?: number;
    published?: boolean;
    snippet?: boolean;
  }>;
  journal?: {
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    pIssn?: string;
    eIssn?: string;
    reviewType?: string;
    noIF?: boolean;
    sciIndexed?: boolean;
    scopusIndexed?: boolean;
    lang?: string;
    hungarian?: boolean;
    published?: boolean;
    oldId?: number;
    snippet?: boolean;
  };
  volume?: string;
  issue?: string;
  firstPage?: string;
  lastPage?: string;
  firstPageOrInternalIdForSort?: string;
  pageLength?: number;
  publishedYear: number;
  subjects?: Array<{
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    published?: boolean;
    snippet?: boolean;
  }>;
  digital?: boolean;
  printed?: boolean;
  sourceYear?: number;
  foreignEdition: boolean;
  foreignLanguage: boolean;
  fullPublication: boolean;
  conferencePublication: boolean;
  nationalOrigin: boolean;
  missingAuthor: boolean;
  oaType: string;
  oaCheckDate: string;
  oaFree: boolean;
  citationCount: number;
  citationCountUnpublished: number;
  citationCountWoOther: number;
  independentCitCountWoOther: number;
  nationalOriginCitationCount?: number;
  foreignEditionCitationCount?: number;
  doiCitationCount?: number;
  wosCitationCount?: number;
  scopusCitationCount?: number;
  wosScopusCitationCount?: number;
  wosScopusCitationCountWoOther?: number;
  wosScopusIndependentCitationCount?: number;
  wosScopusIndependentCitationCountWoOther?: number;
  independentCitationCount?: number;
  selfCitationCount?: number;
  unhandledCitationCount?: number;
  citingPubCount?: number;
  independentCitingPubCount?: number;
  citingPubCountWoOther?: number;
  independentCitingPubCountWoOther?: number;
  unhandledCitingPubCount?: number;
  citedPubCount?: number;
  citedCount?: number;
  ratings?: Array<{
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    ratingType?: {
      otype?: string;
      mtid?: number;
      link?: string;
      label?: string;
      code?: string;
      institute?: {
        otype?: string;
        mtid?: number;
        link?: string;
        label?: string;
        childrenCount?: number;
        allowInstForum?: boolean;
        allowOnlineRegistration?: boolean;
        name?: string;
        nameEng?: string;
        published?: boolean;
        oldId?: number;
        snippet?: boolean;
      };
      published?: boolean;
      snippet?: boolean;
    };
    val?: string;
    listPos?: number;
    rankValue?: number;
    type?: string;
    subject?: {
      otype?: string;
      mtid?: number;
      link?: string;
      label?: string;
      published?: boolean;
      oldId?: number;
      snippet?: boolean;
    };
    ranking?: string;
    calculation?: string;
    published?: boolean;
    snippet?: boolean;
  }>;
  ratingsForSort?: string;
  mtaRatingsForSort?: string;
  hasCitationDuplums?: boolean;
  userChangeableUntil?: string;
  directInstitutesForSort?: string;
  ownerAuthorCount?: number;
  ownerInstituteCount?: number;
  directInstituteCount?: number;
  authorCount?: number;
  contributorCount?: number;
  hasQualityFactor: boolean;
  tempLockerIds?: number[];
  link?: string;
  label?: string;
  template?: string;
  template2?: string;
  publishers?: Array<{
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    name?: string;
    published?: boolean;
    oldId?: number;
    snippet?: boolean;
  }>;
  publishedAt?: Array<{
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    partOf?: {
      otype?: string;
      mtid?: number;
      link?: string;
      label?: string;
      published?: boolean;
      oldId?: number;
      snippet?: boolean;
    };
    published?: boolean;
    oldId?: number;
    snippet?: boolean;
  }>;
  book?: {
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    core?: boolean;
    citation?: boolean;
    publicationPending?: boolean;
    type?: {
      otype?: string;
      mtid?: number;
      link?: string;
      label?: string;
      code?: number;
      otypeName?: string;
      listPosition?: number;
      published?: boolean;
      oldId?: number;
      snippet?: boolean;
    };
    subType?: {
      otype?: string;
      mtid?: number;
      link?: string;
      label?: string;
      name?: string;
      nameEng?: string;
      docType?: {
        otype?: string;
        mtid?: number;
        link?: string;
        label?: string;
        code?: number;
        otypeName?: string;
        listPosition?: number;
        published?: boolean;
        oldId?: number;
        snippet?: boolean;
      };
      listPosition?: number;
      published?: boolean;
      oldId?: number;
      snippet?: boolean;
    };
    category?: {
      otype?: string;
      mtid?: number;
      link?: string;
      label?: string;
      published?: boolean;
      oldId?: number;
      snippet?: boolean;
    };
    languages?: Array<{
      otype?: string;
      mtid?: number;
      link?: string;
      label?: string;
      name?: string;
      nameEng?: string;
      published?: boolean;
      oldId?: number;
      snippet?: boolean;
    }>;
    title?: string;
    publishedYear?: number;
    foreignEdition?: boolean;
    foreignLanguage?: boolean;
    fullPublication?: boolean;
    conferencePublication?: boolean;
    nationalOrigin?: boolean;
    published?: boolean;
    snippet?: boolean;
  };
  fundings?: Array<{
    otype?: string;
    mtid?: number;
    link?: string;
    label?: string;
    published?: boolean;
    snippet?: boolean;
  }>;
  chapterCount?: number;
  fromCitation?: boolean;
}

export interface ExportHeader {
  key: string;
  label: string;
}

export interface ExportHeaderSelections {
  excel: string[];
  word: string[];
  csv: string[];
}

export class Publication implements Publication {
  private _data: Publication;

  constructor(data: Publication) {
    this._data = data;
    return new Proxy(this, {
      get(target: Publication, prop: string) {
        if (prop in target) {
          return target[prop as keyof Publication];
        }
        return target._data[prop as keyof Publication];
      },
      set(target: Publication, prop: string, value: any) {
        if (prop in target) {
          (target as any)[prop] = value;
        } else {
          (target._data as any)[prop] = value;
        }
        return true;
      },
    }) as Publication & Publication;
  }

  // Methods
  isScientific(): boolean {
    return this._data.category?.mtid === 1;
  }

  isBook(): boolean {
    return this._data.type?.label === "Könyv";
  }

  isBookChapter(): boolean {
    return this._data.type?.label === "Könyvrészlet";
  }

  getPageLength(): number {
    if (this._data.pageLength) {
      return this._data.pageLength;
    }
    if (this._data.firstPage && this._data.lastPage) {
      return parseInt(this._data.lastPage) - parseInt(this._data.firstPage) + 1;
    }
    return 0;
  }

  getPublicationType(translations?: Record<string, string>): string {
    const typeMap: Record<string, string> = {
      journalarticle: "journalArticle",
      bookchapter: this._data.subType?.name
        ?.toLowerCase()
        .includes("konferencia")
        ? "conferenceArticle"
        : "bookChapter",
      book: "book",
      patent: "patent",
      thesis: "thesis",
      other: "other",
    };

    const type = this._data.type?.otypeName?.toLowerCase() || "";
    const key = typeMap[type] || "other";

    if (translations && translations[key]) {
      return translations[key];
    }

    // Fallback to English if no translations available
    const fallback: Record<string, string> = {
      journalArticle: "Journal Article",
      conferenceArticle: "Conference Article",
      bookChapter: "Book Chapter",
      book: "Book",
      patent: "Patent",
      thesis: "Thesis",
      other: "Other",
    };

    return fallback[key];
  }

  getRating(empty: string = "N/A"): string {
    return this._data.ratingsForSort || empty;
  }

  getRatingColor(): string {
    switch (this._data.ratingsForSort || "N/A") {
      case "D1":
        return "bg-emerald-600/90"; // softened green
      case "Q1":
        return "bg-blue-600/90"; // softened blue
      case "Q2":
        return "bg-yellow-600/90"; // softened yellow, better with white text
      case "Q3":
        return "bg-amber-600/90"; // softened orange
      case "Q4":
        return "bg-rose-600/90"; // softened red
      default:
        return "bg-gray-600/90"; // softened gray
    }
  }

  getAuthorShare(mtid: string): number {
    const authorships = this._data.authorships || [];
    for (const authorship of authorships) {
      if (
        authorship.mtid === Number(mtid) &&
        authorship.share &&
        authorship.share > 0
      ) {
        return authorship.share;
      }
    }
    return authorships.length > 0 ? 1 / authorships.length : 1;
  }

  isValidArticleType(): boolean {
    const validTypes = [
      "szakcikk",
      "összefoglaló cikk",
      "konferenciaközlemény",
      "rövid közlemény",
      "sokszerzős vagy csoportos szerzőségű szakcikk",
    ];
    return validTypes.includes(this._data.subType?.name?.toLowerCase() || "");
  }

  async getQScore(
    authorShare: number,
    impactFactor: number
  ): Promise<{ qscore: number; source: "articles" | "books" }> {
    if (
      this.otype === "JournalArticle" &&
      this.journal?.reviewType === "REVIEWED" &&
      this.isValidArticleType()
    ) {
      this.qscore =
        impactFactor > 0
          ? Math.max(0.6, impactFactor) * authorShare
          : (this.foreignEdition ? 0.4 : 0.3) * authorShare;
      return { qscore: this.qscore, source: "articles" };
    }

    if (
      this.isBook() ||
      (this.isBookChapter() &&
        (this.subType?.label === "Könyvfejezet (Könyvrészlet)" ||
          this.subType?.label === "Szaktanulmány (Könyvrészlet)"))
    ) {
      const pageLength = this.getPageLength();
      if (pageLength >= 10) {
        const baseScore =
          pageLength >= 100
            ? this.foreignLanguage
              ? 2
              : 1
            : (this.foreignLanguage ? 0.2 : 0.1) * Math.floor(pageLength / 10);
        this.qscore = baseScore * authorShare;
        return { qscore: this.qscore, source: "books" };
      }
    }

    if (this.isBookChapter()) {
      const pageLength = this.getPageLength();
      const isConferencePaper =
        this.subType?.label === "Konferenciaközlemény (Könyvrészlet)" ||
        this.type.label === "Egyéb konferenciaközlemény";

      if (isConferencePaper) {
        this.hasISBNISSN = await this.getISBNISSN();
        if (this.hasISBNISSN && pageLength >= 4) {
          this.qscore = (this.foreignLanguage ? 0.2 : 0.1) * authorShare;
          return { qscore: this.qscore, source: "articles" };
        }
      } else if (pageLength >= 5) {
        this.qscore = (this.foreignLanguage ? 0.2 : 0.1) * authorShare;
        return { qscore: this.qscore, source: "books" };
      }
    }

    this.qscore = 0;
    return { qscore: 0, source: "articles" };
  }

  getIScore(): {
    iscore: number;
    iscoreWos: number;
    scientificCitations: number;
  } {
    let iscoreWos = 0;

    if (this.published && this.citations) {
      this.iscore = this.citations.reduce((score, citation) => {
        if (citation.externalCitation && citation.related?.type?.label) {
          const citingType = citation.related.type.label;
          if (
            [
              "Könyvrészlet",
              "Könyv",
              "Folyóiratcikk",
              "Egyéb konferenciaközlemény",
            ].includes(citingType)
          ) {
            if (
              citation.related.identifiers?.some((i) =>
                ["WoS", "Wos-CSCD (Chinese)"].includes(i.source?.label || "")
              )
            ) {
              iscoreWos++;
              return score + 1;
            }
            return score + 1;
          }
        }
        return score;
      }, 0);

      this.scientificCitationCount = this.citations.reduce(
        (acc, citation) =>
          citation.related?.otype !== "Thesis" &&
          citation.related?.otype !== "PublicationOther"
            ? acc + 1
            : acc,
        0
      );

      return {
        iscore: this.iscore,
        iscoreWos,
        scientificCitations: this.scientificCitationCount,
      };
    }

    return { iscore: 0, iscoreWos: 0, scientificCitations: 0 };
  }

  async getISBNISSN(): Promise<boolean> {
    if (checkIdentifiers(this)) return true;

    if (this.book) {
      if (checkIdentifiers(this.book)) return true;

      const bookResponse = await Fetch.getObjectByLink(this.book.link || "");
      if (!bookResponse.ok || !bookResponse.data?.content) return false;

      const bookData = bookResponse.data.content;
      if (checkIdentifiers(bookData)) return true;

      if (bookData.conference?.link) {
        const confResponse = await Fetch.getObjectByLink(
          bookData.conference.link
        );
        if (
          confResponse.ok &&
          confResponse.data?.content &&
          checkIdentifiers(confResponse.data.content)
        ) {
          return true;
        }
      }

      if (bookData.seriesMembers?.length > 0) {
        for (const series of bookData.seriesMembers) {
          const seriesResponse = await Fetch.getObjectByLink(series.link);
          if (seriesResponse.ok && seriesResponse.data?.content) {
            const seriesContent = seriesResponse.data.content;
            if (
              seriesContent.issns?.length > 0 ||
              seriesContent.pIssn ||
              seriesContent.eIssn ||
              !seriesContent.noISSN
            ) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }
}

const checkIdentifiers = (object: any): boolean => {
  return (
    object?.identifiers?.some(
      (id: any) => id.source?.name === "ISBN" || id.source?.name === "ISSN"
    ) || false
  );
};
